import React from "react";
import Button from "components/button/Button";
import Spinner from "images/image-component/Spinner";
import RedeemModal from "modals/redeem-modal/RedeemModal";
import StripeOnboardingModal from "modals/stripe-onboarding-modal/StripeOnboardingModal";
import ColumnCounterpartyModal from "modals/column-counterparty-modal/ColumnCounterpartyModal";
import LoadingModalV1 from "modals/loading/LoadingModalV1";
import SuccessModal from "modals/success-modal/SuccessModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import WarningModal from "modals/warning-modal/WarningModal";
import { MIN_REDEEM } from "features/redeem/useRedeemData";
import { useBalanceCta } from "./useBalanceCta";

type SubmitRedeemFn = (
  payload: {
    merchantId: string;
    treasuryId: string;
    amountInVip: number;
    settlementToken?: string;
  },
  callbacks: { onSuccess: () => void; onError: (err: any) => void },
) => void;

export interface BalanceCtaProps {
  merchantId: string;
  treasuryId: string;
  /** Available balance in the selected token, net of that token's pending redeems. */
  balance: number;
  tokenSymbol: string;
  settlementToken?: string;
  pendingCount: number;
  // True while balance / pendingCount are still loading, so the redeem
  // guards aren't evaluated against placeholder zeros on the Stripe return.
  guardsLoading?: boolean;
  submitRedeem: SubmitRedeemFn;
}

function BalanceCta({
  merchantId,
  treasuryId,
  balance,
  tokenSymbol,
  settlementToken,
  pendingCount,
  guardsLoading,
  submitRedeem,
}: BalanceCtaProps) {
  const {
    handleRedeemClick,
    handleRedeemConfirm,
    handleColumnSubmit,
    handlePayoutsClick,
    payoutsLoading,
    columnSubmitting,
    onboardingUrl,
    onboardingVariant,
    onboardingSkip,
    onboardingSkipLabel,
    loadingMsg,
    errorMsg,
    successMsg,
    pendingReviewMsg,
    showRedeem,
    closeRedeemModal,
    showRedeemBlocked,
    redeemBlockedMessage,
    closeRedeemBlockedModal,
    showColumnForm,
    closeColumnForm,
    showStripeOnboarding,
    closeStripeOnboardingModal,
    showPendingReview,
    closePendingReviewModal,
    showLoading,
    showSuccess,
    closeSuccessModal,
    showError,
    closeErrorModal,
  } = useBalanceCta({
    merchantId,
    treasuryId,
    balance,
    tokenSymbol,
    settlementToken,
    pendingCount,
    guardsLoading,
    submitRedeem,
  });

  return (
    <>
      <div className="merchant-profile__balance-cta">
        <Button
          variant="ghost"
          size="md"
          onClick={handlePayoutsClick}
          disabled={payoutsLoading}
        >
          {payoutsLoading ? (
            <Spinner size={16} color="currentColor" />
          ) : (
            "Payouts"
          )}
        </Button>
        <Button variant="primary" size="md" onClick={handleRedeemClick}>
          Redeem
        </Button>
      </div>

      {showRedeem && (
        <RedeemModal
          isOpen={showRedeem}
          balance={balance}
          tokenSymbol={tokenSymbol}
          minAmount={MIN_REDEEM}
          onClose={closeRedeemModal}
          onConfirm={handleRedeemConfirm}
        />
      )}
      {showRedeemBlocked && (
        <ErrorModal
          isOpen={showRedeemBlocked}
          errorMsg={redeemBlockedMessage}
          handleClose={closeRedeemBlockedModal}
        />
      )}
      {showColumnForm && (
        <ColumnCounterpartyModal
          isOpen={showColumnForm}
          submitting={columnSubmitting}
          onClose={closeColumnForm}
          onSubmit={handleColumnSubmit}
        />
      )}
      {showStripeOnboarding && (
        <StripeOnboardingModal
          isOpen={showStripeOnboarding}
          onboardingUrl={onboardingUrl}
          source="redeem"
          variant={onboardingVariant}
          onSkip={onboardingSkip}
          skipLabel={onboardingSkipLabel}
          onClose={closeStripeOnboardingModal}
        />
      )}
      {showPendingReview && (
        <WarningModal
          isOpen={showPendingReview}
          title="Verification in progress"
          message={pendingReviewMsg}
          handleClose={closePendingReviewModal}
        />
      )}
      <LoadingModalV1 isOpen={showLoading} loadingMsg={loadingMsg} />
      {showSuccess && (
        <SuccessModal
          isOpen={showSuccess}
          message={successMsg}
          handleClose={closeSuccessModal}
        />
      )}
      {showError && (
        <ErrorModal
          isOpen={showError}
          errorMsg={errorMsg}
          handleClose={closeErrorModal}
        />
      )}
    </>
  );
}

export default BalanceCta;
